import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../../services/api';

interface LogDetailViewProps {
  log: any;
  open: boolean;
  onClose: () => void;
}

const LogDetailView: React.FC<LogDetailViewProps> = ({ log, open, onClose }) => {
  if (!log) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Anonymization Log Detail</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>
          ID: {log.id}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Time: {new Date(log.timestamp * 1000).toLocaleString()}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Type: {log.entry_type}
        </Typography>
        <Typography 
          variant="subtitle2" 
          gutterBottom
          color={log.anonymization_enabled ? "success.main" : "text.secondary"}
          sx={{ fontWeight: log.anonymization_enabled ? 'bold' : 'normal' }}
        >
          Anonymization: {log.anonymization_enabled ? "Enabled" : "Disabled"}
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Original Request</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5' 
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{log.original_text}</pre>
              </Paper>
            </AccordionDetails>
          </Accordion>
          
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Anonymized Request</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  backgroundColor: '#f8f8f8' 
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{log.anonymized_text}</pre>
              </Paper>
            </AccordionDetails>
          </Accordion>
          
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Raw Response</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  backgroundColor: '#f8f8f8' 
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{log.response_text}</pre>
              </Paper>
            </AccordionDetails>
          </Accordion>
          
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">De-anonymized Response</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5' 
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{log.deanonymized_text}</pre>
              </Paper>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AnonymizationLogs: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [limit, setLimit] = useState(20);
  
  // Fetch logs
  const { 
    data: logs, 
    isLoading: logsLoading, 
    isError: logsError,
    refetch: refetchLogs
  } = useQuery(
    'anonymizationLogs', 
    () => api.config.getAnonymizationLogs(limit),
    {
      refetchInterval: false,
      onError: (error) => {
        console.error('Failed to fetch anonymization logs', error);
      }
    }
  );
  
  // Mutation for clearing logs
  const clearLogsMutation = useMutation(api.config.clearAnonymizationLogs, {
    onSuccess: () => {
      queryClient.invalidateQueries('anonymizationLogs');
    },
    onError: (error) => {
      console.error('Failed to clear logs', error);
    }
  });
  
  // Handle opening log details
  const handleOpenLogDetail = (log: any) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };
  
  // Handle closing log details
  const handleCloseLogDetail = () => {
    setDialogOpen(false);
  };
  
  // Handle clearing logs
  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all anonymization logs?')) {
      clearLogsMutation.mutate();
    }
  };
  
  if (logsLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  
  if (logsError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load anonymization logs. Please try again.
      </Alert>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Anonymization Logs
        </Typography>
        <Box>
          <Tooltip title="Refresh logs">
            <IconButton 
              onClick={() => refetchLogs()} 
              size="small" 
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleClearLogs}
            disabled={clearLogsMutation.isLoading || !logs || logs.length === 0}
          >
            Clear Logs
          </Button>
        </Box>
      </Box>
      
      {logs && logs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No anonymization logs available.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Logs will appear here when anonymized requests are made with logging enabled.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Anonymized</TableCell>
                <TableCell>Request Preview</TableCell>
                <TableCell align="center">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs && logs.map((log: any) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    {new Date(log.timestamp * 1000).toLocaleString()}
                  </TableCell>
                  <TableCell>{log.entry_type}</TableCell>
                  <TableCell>
                    {log.anonymization_enabled !== undefined ? (
                      <Typography
                        variant="body2"
                        color={log.anonymization_enabled ? "success.main" : "text.secondary"}
                        sx={{ fontWeight: log.anonymization_enabled ? 'bold' : 'normal' }}
                      >
                        {log.anonymization_enabled ? "Yes" : "No"}
                      </Typography>
                    ) : "Unknown"}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Original Text">
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: '300px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {log.original_text.length > 50 
                          ? log.original_text.substring(0, 50) + '...' 
                          : log.original_text}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenLogDetail(log)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <LogDetailView
        log={selectedLog}
        open={dialogOpen}
        onClose={handleCloseLogDetail}
      />
    </Box>
  );
};

export default AnonymizationLogs;
